import styles from './applied-filters.module.scss'
import {
  FILTER_BODY_STYLES,
  FILTER_CAPACITY_PAYLOAD,
  FILTER_DRIVE_TYPES,
  FILTER_ELECTRIC_BATTERY_CAPACITY,
  FILTER_ELECTRIC_FASTEST_CHARGE_RATE,
  FILTER_ELECTRIC_HOME_CHARGE_TIME,
  FILTER_ELECTRIC_RANGE,
  FILTER_FEATURES,
  FILTER_FUEL_TYPES,
  FILTER_INITIAL_PAYMENT_MAX_DEFAULT,
  FILTER_LEASE_TYPES_PERSONAL,
  FILTER_MANUFACTURERS,
  FILTER_MONTHLY_PAYMENT_MAX_DEFAULT,
  FILTER_ROOF_HEIGHT,
  FILTER_TRANSMISSION_TYPES,
  FILTER_VEHICLE_TYPES_VANS,
  FILTER_WHEELBASE,
} from '@/lib/constants'
import { FILTERS } from '@/lib/filters'
import {
  addVatToPrice,
  convertToMetres,
  convertToTonnes,
  formatWithCommas,
  fuelTypeAbbreviation,
  getElectricFilterLabelPrettyName,
  getTermsFilterName,
} from '@/lib/utilities/search'

export const AppliedFilters = ({
  search,
  ranges,
  vehicleTags,
  vehiclePriceTags,
  hideDefaults = false,
  separator = ', ',
  className = '',
  filterLimit = 3,
}) => {
  let allAppliedFilters = []

  const getMonthlyPayment = () => {
    const min = search.searchRequest.monthlyPaymentMin
    const max = search.searchRequest.monthlyPaymentMax

    let maxValue = Math.floor(
      search.searchRequest.leaseTypes?.[0] === FILTER_LEASE_TYPES_PERSONAL
        ? addVatToPrice(search.searchRequest.monthlyPaymentMax)
        : search.searchRequest.monthlyPaymentMax,
    )
    const minValue = Math.floor(
      search.searchRequest.leaseTypes?.[0] === FILTER_LEASE_TYPES_PERSONAL
        ? addVatToPrice(search.searchRequest.monthlyPaymentMin)
        : search.searchRequest.monthlyPaymentMin,
    )

    if (min === undefined && max > 8000000) {
      return 'Any Budget'
    }

    if (!!min && !!max) {
      return `£${formatWithCommas(minValue)}${max > FILTER_MONTHLY_PAYMENT_MAX_DEFAULT ? '+' : ` to £${formatWithCommas(maxValue)}`}`
    }

    if (min === undefined && !!max) {
      return `£0 to £${maxValue > FILTER_MONTHLY_PAYMENT_MAX_DEFAULT ? formatWithCommas(FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) : formatWithCommas(maxValue)}`
    }

    return 'Any Budget'
  }

  const getInitialPayment = () => {
    const min = search.searchRequest.initialPaymentMin
    const max = search.searchRequest.initialPaymentMax

    let maxValue = Math.floor(
      search.searchRequest.leaseTypes?.[0] === FILTER_LEASE_TYPES_PERSONAL
        ? addVatToPrice(search.searchRequest.initialPaymentMax)
        : search.searchRequest.initialPaymentMax,
    )
    const minValue = Math.floor(
      search.searchRequest.leaseTypes?.[0] === FILTER_LEASE_TYPES_PERSONAL
        ? addVatToPrice(search.searchRequest.initialPaymentMin)
        : search.searchRequest.initialPaymentMin,
    )

    if (min === undefined && max > 8000000) {
      return 'Any Budget'
    }

    if (!!min && !!max) {
      return `£${minValue}${max > FILTER_INITIAL_PAYMENT_MAX_DEFAULT ? '+' : ` to £${maxValue}`}`
    }

    if (min === undefined && !!max) {
      return `£0 to £${formatWithCommas(FILTER_INITIAL_PAYMENT_MAX_DEFAULT)}`
    }

    return 'Any Budget'
  }

  const getCapacityRange = (min, max, capacityType = null) => {
    const onlyMaxValueExists = (min === 0 || min === null) && !!max

    if (capacityType === FILTER_CAPACITY_PAYLOAD) {
      return `${onlyMaxValueExists ? '0' : convertToTonnes(min)}t to ${convertToTonnes(max)}t`
    } else {
      return `${onlyMaxValueExists ? '0' : convertToMetres(min)}m to ${convertToMetres(max)}m`
    }
  }

  const getRangeName = filter => {
    return ranges?.filter(range => range.id === filter)?.[0]?.name || null
  }

  const getVehicleTagName = filter => vehicleTags?.filter(tag => tag.id === filter)[0]?.name

  const getFilterName = (filter, type) => {
    const foundFilter = FILTERS[type].filter(item => item.id === filter)

    return foundFilter.length > 0 && !!foundFilter[0].name ? foundFilter[0].name : null
  }

  const getPerformanceText = id => {
    const filters = FILTERS.performance.filter(filter => filter.id === id)
    const filterName = filters.length > 0 && !!filters[0].name ? filters[0].name : null

    if (filterName) {
      if (id >= 1 && id <= 25) {
        return `CO2: ${filterName}g/km`
      }

      if (id >= 26 && id <= 36) {
        return `MPG: ${filterName}mpg`
      }

      if ((id >= 37 && id <= 44) || (id >= 441 && id <= 450)) {
        return `0 to 62: ${filterName}s`
      }

      if (id >= 45 && id <= 53) {
        return `Min Engine: ${filterName}L`
      }

      if (id >= 54 && id <= 62) {
        return `Max Engine: ${filterName}L`
      }
    }
  }

  const getSpecFilterName = id => {
    let specArray = []

    FILTERS.spec.map(item => {
      if (item.subFilters) {
        item.subFilters.map(() => specArray.push(...item.subFilters))
      }
      return specArray.push(item)
    })

    const filteredArray = specArray.filter(spec => spec.id === id)

    let name = filteredArray.length > 0 && !!filteredArray[0].name ? filteredArray[0].name : null

    if (id >= 43016 && id <= 43022) {
      name = `${name}" Alloys`
    }

    return name
  }

  if (!hideDefaults && !!search.searchRequest.vehicleTypes) {
    search.searchRequest.vehicleTypes.forEach(filter =>
      allAppliedFilters.push(filter === FILTER_VEHICLE_TYPES_VANS ? 'Vans' : 'Cars'),
    )
  }

  if (!hideDefaults && !search.searchRequest.vehicleTypes) {
    allAppliedFilters.push('Cars')
  }

  if (!hideDefaults && search.searchRequest.leaseTypes.length > 0) {
    search.searchRequest.leaseTypes.forEach(filter =>
      allAppliedFilters.push(filter === 1 ? 'Business' : 'Personal'),
    )
  }

  if (!!search.searchRequest.vehicleTags && search.searchRequest.vehicleTags.length > 0) {
    search.searchRequest.vehicleTags.forEach(filter => {
      const filterName = getVehicleTagName(filter)

      if (!filterName) {
        return false
      }

      allAppliedFilters.push(filterName)
    })
  }

  if (!!search.searchRequest.vehiclePriceTags && search.searchRequest.vehiclePriceTags.length > 0) {
    search.searchRequest.vehiclePriceTags.map(filter => {
      const filterDetails = vehiclePriceTags?.filter(
        tag => tag.id === filter.vehiclePriceTagCategoryId,
      )
      const categories = vehiclePriceTags?.filter(
        tag => tag.id === filter.vehiclePriceTagCategoryId,
      )

      if (filterDetails?.length === 0) {
        return false
      }

      if (filter.vehiclePriceTagCategoryId === 3 && filterDetails?.length > 0) {
        const category = vehiclePriceTags.find(vpt => vpt.id === 3)

        const selectedLeadTimeTags = filter.vehiclePriceTagIds.map(tagId => {
          return category.vehiclePriceTags?.find(t => t.id === tagId)
        })

        return allAppliedFilters.push(
          selectedLeadTimeTags
            .sort((a, b) => a.priority - b.priority)
            .map(i => i?.shortLabel || i?.name)
            .join(', '),
        )
      }

      if (!!categories && categories.length > 0) {
        allAppliedFilters.push(categories[0].name)
      }
    })
  }

  {
    search.searchRequest.terms.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.terms.map(filter => getTermsFilterName(filter)).join(separator),
      )
  }

  {
    search.searchRequest.mileages.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.mileages
          .map(filter => `${formatWithCommas(filter)} mi`)
          .join(separator),
      )
  }

  {
    search.searchRequest.manufacturers.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.manufacturers.map(filter =>
          getFilterName(filter, FILTER_MANUFACTURERS),
        ),
      )
  }
  {
    search.searchRequest.ranges.length > 0 &&
      allAppliedFilters.push(search.searchRequest.ranges.map(filter => getRangeName(filter)))
  }

  {
    search.searchRequest.fuelTypes.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.fuelTypes.map(filter =>
          fuelTypeAbbreviation(getFilterName(filter, FILTER_FUEL_TYPES)),
        ),
      )
  }

  {
    search.searchRequest.driveTypes.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.driveTypes.map(filter => getFilterName(filter, FILTER_DRIVE_TYPES)),
      )
  }

  {
    search.searchRequest.transmissionTypes.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.transmissionTypes.map(filter =>
          getFilterName(filter, FILTER_TRANSMISSION_TYPES),
        ),
      )
  }

  {
    search.searchRequest.bodyStyles.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.bodyStyles.map(filter => getFilterName(filter, FILTER_BODY_STYLES)),
      )
  }

  {
    search.searchRequest.doors.length > 0 &&
      allAppliedFilters.push(search.searchRequest.doors.map(filter => `${filter} Doors`))
  }

  {
    search.searchRequest.seats.length > 0 &&
      allAppliedFilters.push(search.searchRequest.seats.map(filter => `${filter} Seats`))
  }

  {
    search.searchRequest.performance.length > 0 &&
      search.searchRequest.performance[0] !== 0 &&
      allAppliedFilters.push(
        search.searchRequest.performance.map(filter => getPerformanceText(filter)),
      )
  }

  {
    search.searchRequest.spec.length > 0 &&
      allAppliedFilters.push(search.searchRequest.spec.map(filter => getSpecFilterName(filter)))
  }

  {
    search.searchRequest.features &&
      search.searchRequest.features.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.features.map(filter => getFilterName(filter, FILTER_FEATURES)),
      )
  }

  {
    search.searchRequest.wheelbase &&
      search.searchRequest.wheelbase.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.wheelbase.map(
          filter => `Wheelbase: ${getFilterName(filter, FILTER_WHEELBASE)}`,
        ),
      )
  }

  {
    search.searchRequest.roofHeight &&
      search.searchRequest.roofHeight.length > 0 &&
      allAppliedFilters.push(
        search.searchRequest.roofHeight.map(
          filter => `Roof Height: ${getFilterName(filter, FILTER_ROOF_HEIGHT)}`,
        ),
      )
  }

  if (!!search.searchRequest.payloadMin || !!search.searchRequest.payloadMax) {
    allAppliedFilters.push(
      `Payload: ${getCapacityRange(
        search.searchRequest.payloadMin,
        search.searchRequest.payloadMax,
        FILTER_CAPACITY_PAYLOAD,
      )}`,
    )
  }

  if (!!search.searchRequest.loadHeightMin || !!search.searchRequest.loadHeightMax) {
    allAppliedFilters.push(
      `Load Height: ${getCapacityRange(
        search.searchRequest.loadHeightMin,
        search.searchRequest.loadHeightMax,
      )}`,
    )
  }

  if (!!search.searchRequest.loadLengthMin || !!search.searchRequest.loadLengthMax) {
    allAppliedFilters.push(
      `Load Length: ${getCapacityRange(
        search.searchRequest.loadLengthMin,
        search.searchRequest.loadLengthMax,
      )}`,
    )
  }

  if (!!search.searchRequest.loadWidthMin || !!search.searchRequest.loadWidthMax) {
    allAppliedFilters.push(
      `Load Width: ${getCapacityRange(
        search.searchRequest.loadWidthMin,
        search.searchRequest.loadWidthMax,
      )}`,
    )
  }

  if (search.searchRequest.inStock) {
    allAppliedFilters.push('In Stock Only')
  }

  if (search.searchRequest.electricBatteryCapacity?.length) {
    allAppliedFilters.push(
      getElectricFilterLabelPrettyName(
        FILTER_ELECTRIC_BATTERY_CAPACITY,
        search.searchRequest.electricBatteryCapacity[0],
      ),
    )
  }

  if (search.searchRequest.electricFastestChargeRate?.length) {
    allAppliedFilters.push(
      getElectricFilterLabelPrettyName(
        FILTER_ELECTRIC_FASTEST_CHARGE_RATE,
        search.searchRequest.electricFastestChargeRate[0],
      ),
    )
  }

  if (search.searchRequest.electricHomeChargeTime?.length) {
    allAppliedFilters.push(
      getElectricFilterLabelPrettyName(
        FILTER_ELECTRIC_HOME_CHARGE_TIME,
        search.searchRequest.electricHomeChargeTime[0],
      ),
    )
  }

  if (search.searchRequest.electricRange?.length) {
    allAppliedFilters.push(
      getElectricFilterLabelPrettyName(
        FILTER_ELECTRIC_RANGE,
        search.searchRequest.electricRange[0],
      ),
    )
  }

  if ((hideDefaults && getMonthlyPayment() !== 'Any Budget') || !hideDefaults) {
    allAppliedFilters.push(`Monthly: ${getMonthlyPayment()}`)
  }

  if ((hideDefaults && getInitialPayment() !== 'Any Budget') || !hideDefaults) {
    allAppliedFilters.push(`Upfront: ${getInitialPayment()}`)
  }

  if (!!search.searchRequest.searchTerm && search.searchRequest.searchTerm.length > 0) {
    allAppliedFilters.push(`Text Search: ${search.searchRequest.searchTerm}`)
  }

  return (
    <div className={`${styles.container} ${className} applied-filters`}>
      {allAppliedFilters
        .slice(0, filterLimit ? filterLimit : allAppliedFilters.length)
        .join(separator)}
      {filterLimit &&
        allAppliedFilters.slice(filterLimit).length > 0 &&
        ` + ${allAppliedFilters.slice(filterLimit).length} more`}
    </div>
  )
}
